import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  ChevronDown,
  Eye,
  Download,
  MoreVertical,
  FileText,
  X,
} from "lucide-react";
import API_BASE_URL from "../api";

// ⚠️ Adjust this to match your real backend route once it exists.
const API_BASE = `${API_BASE_URL}/api/inputHistory`;

const STATUS_OPTIONS = ["All Status", "Verified", "Pending"];
const FUEL_OPTIONS = ["All Fuel Types", "Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const PAGE_SIZE = 10;

function StatusPill({ status }) {
  const isVerified = status === "Verified";
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        isVerified ? "text-emerald-600" : "text-amber-500"
      }`}
    >
      <span className="text-[8px]">●</span>
      {status}
    </span>
  );
}

export default function AdminCertificates() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [fuelFilter, setFuelFilter] = useState("All Fuel Types");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchCertificates = async () => {
      setLoading(true);
      setError(null);
      try {
        // ⚠️ Placeholder endpoint — replace with your real "list all certificates" route.
        const res = await fetch(`${API_BASE}/totalCertificateGenerated`).then((r) => r.json());
        console.log("ressss",res)
        setCertificates(res?.TotalCertificateGenerated || []);
      } catch (err) {
        console.error("Failed to load certificates:", err);
        setError("Couldn't load certificates. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
  }, []);

  const filtered = useMemo(() => {
    return certificates.filter((c) => {
      const matchesSearch =
        !search ||
        c.certificateNumber?.toLowerCase().includes(search.toLowerCase()) ||
        c.vehicleNumber?.toLowerCase().includes(search.toLowerCase()) ||
        c.ownerName?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "All Status" || c.status === statusFilter;
      const matchesFuel = fuelFilter === "All Fuel Types" || c.fuelType === fuelFilter;
      return matchesSearch && matchesStatus && matchesFuel;
    });
  }, [certificates, search, statusFilter, fuelFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, fuelFilter]);

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Green Certificates</h3>
          <p className="text-sm text-gray-500">
            {loading ? "Loading…" : `${filtered.length} certificate${filtered.length !== 1 ? "s" : ""} found`}
          </p>
        </div>
        <button className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors">
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by certificate no, vehicle no, or owner…"
            className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 text-sm rounded-xl border border-gray-200 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        <div className="relative">
          <select
            value={fuelFilter}
            onChange={(e) => setFuelFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 text-sm rounded-xl border border-gray-200 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
          >
            {FUEL_OPTIONS.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {error && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs">
                <th className="px-6 py-3 font-medium">Certificate No.</th>
                <th className="px-4 py-3 font-medium">Vehicle No.</th>
                <th className="px-4 py-3 font-medium">Fuel Type</th>
                <th className="px-4 py-3 font-medium">Owner Name</th>
                <th className="px-4 py-3 font-medium">CO₂ Emission (kg)</th>
                <th className="px-4 py-3 font-medium">Distance (km)</th>
                <th className="px-4 py-3 font-medium">Issue Date</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center text-gray-400">
                    Loading certificates…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <FileText size={24} className="text-gray-300" />
                      No certificates match your filters
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((row, i) => (
                  <tr
                    key={row.certificateNumber || i}
                    className={i !== paginated.length - 1 ? "border-b border-gray-50" : ""}
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">{row.certificateNumber || "—"}</td>
                    <td className="px-4 py-4 text-gray-600">{row.vehicleNumber}</td>
                    <td className="px-4 py-4 text-gray-700">{row.fuelType || "—"}</td>
                    <td className="px-4 py-4 text-gray-700">{row.ownerName}</td>
                    <td className="px-4 py-4 text-gray-700">
                      {typeof row.carbonFootprint === "number" ? row.carbonFootprint.toFixed(2) : "—"}
                    </td>
                    <td className="px-4 py-4 text-gray-700">{row.distance ?? "—"}</td>
                    <td className="px-4 py-4 text-gray-500">
                      {row.issueDate ? new Date(row.issueDate).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-4">
                      <StatusPill status={row.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-3 text-gray-400">
                        {row.pdfUrl && (
                          <>
                            <a href={row.pdfUrl} target="_blank" rel="noreferrer" title="View certificate">
                              <Eye size={16} className="cursor-pointer hover:text-gray-600" />
                            </a>
                            <a href={row.pdfUrl} download title="Download certificate">
                              <Download size={16} className="cursor-pointer hover:text-gray-600" />
                            </a>
                          </>
                        )}
                        <MoreVertical size={16} className="cursor-pointer hover:text-gray-600" />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-50 text-sm text-gray-500">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Prev
              </button>
              <span className="text-gray-400 text-xs">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}